const WEBSITE_URL_PROTOCOL_REGEX = /^[a-z][a-z\d+\-.]*:\/\//i;
const WEBSITE_URL_SCHEME_REGEX = /^[a-z][a-z\d+\-.]*:/i;
const WEBSITE_URL_HTTP_SCHEME_WITHOUT_COLON_REGEX = /^https?\/{1,2}/i;

export function normalizeWebsiteUrlWithDefaultHttps(rawWebsiteUrl: string): string {
  const trimmedWebsiteUrl = rawWebsiteUrl.trim();
  if (
    WEBSITE_URL_HTTP_SCHEME_WITHOUT_COLON_REGEX.test(trimmedWebsiteUrl) ||
    hasSchemeWithoutProtocol(trimmedWebsiteUrl)
  ) {
    throw new TypeError("Invalid URL");
  }
  const hasProtocol = WEBSITE_URL_PROTOCOL_REGEX.test(trimmedWebsiteUrl);
  const websiteUrlWithProtocol = hasProtocol ? trimmedWebsiteUrl : `https://${trimmedWebsiteUrl}`;
  return new URL(websiteUrlWithProtocol).toString();
}

function hasSchemeWithoutProtocol(value: string): boolean {
  if (!WEBSITE_URL_SCHEME_REGEX.test(value) || WEBSITE_URL_PROTOCOL_REGEX.test(value)) {
    return false;
  }

  const schemeCandidate = value.slice(0, value.indexOf(":")).toLowerCase();
  if (schemeCandidate === "http" || schemeCandidate === "https") {
    return true;
  }

  return !isHostPortUrl(value);
}

function isHostPortUrl(value: string): boolean {
  const colonIndex = value.indexOf(":");
  const host = value.slice(0, colonIndex).toLowerCase();
  const suffix = value.slice(colonIndex + 1);
  const delimiterIndex = suffix.search(/[/?#]/);
  const port = delimiterIndex === -1 ? suffix : suffix.slice(0, delimiterIndex);
  if (!/^\d+$/.test(port)) {
    return false;
  }
  if (host !== "localhost" && !host.includes(".") && !isIPv4Address(host)) {
    return false;
  }

  try {
    new URL(`https://${value}`);
    return true;
  } catch {
    return false;
  }
}

type PublicWebsiteUrlErrorMessages = {
  unsupportedProtocol: string;
  credentials: string;
  invalidHostname: string;
  publicHostname: string;
};

const DEFAULT_PUBLIC_WEBSITE_URL_ERROR_MESSAGES: PublicWebsiteUrlErrorMessages = {
  unsupportedProtocol: "Unsupported website URL protocol.",
  credentials: "Website URL must not include credentials.",
  invalidHostname: "Website URL must use a valid hostname.",
  publicHostname: "Website URL must use a public hostname.",
};

export function normalizePublicWebsiteUrl(
  rawWebsiteUrl: string,
  errorMessages: PublicWebsiteUrlErrorMessages = DEFAULT_PUBLIC_WEBSITE_URL_ERROR_MESSAGES,
): string {
  const normalizedWebsiteUrl = normalizeWebsiteUrlWithDefaultHttps(rawWebsiteUrl);
  const url = new URL(normalizedWebsiteUrl);
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error(errorMessages.unsupportedProtocol);
  }
  validatePublicWebsiteUrl(url, errorMessages);
  return normalizedWebsiteUrl;
}

export function hasValidUrlHostname(url: URL): boolean {
  const hostname = url.hostname.replace(/^\[|\]$/g, "").toLowerCase();

  if (hostname.length === 0) {
    return false;
  }

  if (hostname.includes(":")) {
    return isIPv6Address(hostname);
  }

  if (isIPv4Address(hostname)) {
    return true;
  }

  const normalizedHostname = hostname.endsWith(".") ? hostname.slice(0, -1) : hostname;
  if (normalizedHostname.length === 0 || normalizedHostname.length > 253) {
    return false;
  }

  return normalizedHostname.split(".").every((label) => {
    return (
      label.length > 0 &&
      label.length <= 63 &&
      /^[a-z0-9-]+$/.test(label) &&
      !label.startsWith("-") &&
      !label.endsWith("-")
    );
  });
}

export function isValidHttpUrlWithHostname(value: string): boolean {
  const trimmedValue = value.trim();
  if (
    WEBSITE_URL_SCHEME_REGEX.test(trimmedValue) &&
    !WEBSITE_URL_PROTOCOL_REGEX.test(trimmedValue)
  ) {
    return false;
  }

  try {
    const url = new URL(trimmedValue);
    return (url.protocol === "http:" || url.protocol === "https:") && hasValidUrlHostname(url);
  } catch {
    return false;
  }
}

function validatePublicWebsiteUrl(url: URL, errorMessages: PublicWebsiteUrlErrorMessages): void {
  if (url.username || url.password) {
    throw new Error(errorMessages.credentials);
  }

  if (!hasValidUrlHostname(url)) {
    throw new Error(errorMessages.invalidHostname);
  }

  const hostname = url.hostname.replace(/^\[|\]$/g, "").toLowerCase();
  if (
    hostname === "localhost" ||
    hostname === "::1" ||
    hostname.endsWith(".local") ||
    isPrivateIPv4Address(hostname)
  ) {
    throw new Error(errorMessages.publicHostname);
  }

  if (!hostname.includes(".") && !isIPv4Address(hostname)) {
    throw new Error(errorMessages.publicHostname);
  }
}

function isPrivateIPv4Address(hostname: string): boolean {
  const octets = getIPv4Octets(hostname);
  if (!octets) {
    return false;
  }
  const [first, second] = octets;
  return (
    first === 0 ||
    first === 10 ||
    first === 127 ||
    (first === 169 && second === 254) ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168)
  );
}

function isIPv6Address(hostname: string): boolean {
  if (!/^[0-9a-f:.]+$/.test(hostname)) {
    return false;
  }

  // URL parsing already rejects malformed bracketed IPv6 hosts. This check only
  // distinguishes them from DNS hostnames before label validation.
  return hostname.includes(":");
}

function isIPv4Address(hostname: string): boolean {
  return getIPv4Octets(hostname) !== null;
}

function getIPv4Octets(hostname: string): [number, number, number, number] | null {
  const parts = hostname.split(".");
  if (parts.length !== 4) {
    return null;
  }

  const octets = parts.map((part) => {
    if (!/^\d+$/.test(part)) {
      return null;
    }
    const value = Number(part);
    if (value < 0 || value > 255) {
      return null;
    }
    return value;
  });

  if (octets.some((octet) => octet == null)) {
    return null;
  }
  return octets as [number, number, number, number];
}
